import React, { useState } from 'react';
import { ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { MortgageData } from '../CalculatorTabs';
import { formatCurrency, formatInterestRate } from '../utils/formatters';

interface ClosingDisclosurePreviewProps {
  mortgageData: MortgageData;
}

/* ─── helpers ─────────────────────────────────────────────────────────── */

const today = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

const SectionHeader: React.FC<{ children: React.ReactNode; dark?: boolean }> = ({ children, dark }) => (
  <div
    className={`px-4 py-2 font-bold text-sm uppercase tracking-wide border-b ${
      dark
        ? 'bg-foreground text-background'
        : 'bg-muted text-muted-foreground'
    }`}
  >
    {children}
  </div>
);

const Row: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
  bold?: boolean;
  indent?: boolean;
  sub?: boolean;
}> = ({ label, value, bold, indent, sub }) => (
  <div
    className={`flex justify-between items-baseline px-4 py-1.5 text-sm border-b border-border/40 ${
      bold ? 'font-bold' : ''
    } ${indent ? 'pl-8' : ''} ${sub ? 'text-muted-foreground text-xs' : ''}`}
  >
    <span>{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

/* ─── component ───────────────────────────────────────────────────────── */

const ClosingDisclosurePreview: React.FC<ClosingDisclosurePreviewProps> = ({ mortgageData }) => {
  const [expanded, setExpanded] = useState(false);

  const loanAmount = mortgageData.homePrice - mortgageData.downPayment;
  const cc = mortgageData.closingCosts;
  const mp = mortgageData.monthlyPayment;
  const pi = mp.principal + mp.interest;
  const escrow = mp.taxes + mp.insurance;
  const totalMonths = mortgageData.loanTerm * 12;
  const totalPayments = mp.total * totalMonths;
  const totalInterest = (pi * totalMonths) - loanAmount;
  const financeCharge = totalInterest + (cc.fundingFee || 0);

  const sellerCredit = cc.sellerCreditAmount || 0;
  const grossClosingCosts = cc.total;
  const netClosingCosts = grossClosingCosts - sellerCredit;
  const cashToClose = cc.totalCashNeeded || (mortgageData.downPayment + netClosingCosts);

  const loanCosts = (cc.loanOrigination || 0) + (cc.appraisalFee || 0) + (cc.titleServices || 0);
  const otherCosts =
    (cc.governmentRecordingCharges || 0) +
    (cc.prepaids || 0) +
    (cc.initialEscrow || 0) +
    (cc.other || 0);

  const propertyAddress = [mortgageData.city, mortgageData.state, mortgageData.zipCode]
    .filter(Boolean)
    .join(', ') || '—';

  return (
    <div className="space-y-2">
      <Button
        variant="outline"
        className="w-full flex items-center justify-between"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="font-semibold">Closing Disclosure Preview</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </Button>

      {expanded && (
        <div className="border border-border rounded-lg overflow-hidden bg-card text-card-foreground print:border-black">
          {/* ── Disclaimer ── */}
          <div className="flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 text-xs font-medium">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            Sample — Not an Official Document. For illustrative purposes only.
          </div>

          {/* ═══════════ PAGE 1 — Closing Information & Loan Terms ═══════════ */}
          <SectionHeader dark>Closing Disclosure</SectionHeader>

          {/* Closing Information */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-0 text-xs border-b">
            <div className="px-4 py-2 border-r border-border/40">
              <span className="text-muted-foreground block">Date Issued</span>
              <span className="font-semibold">{today()}</span>
            </div>
            <div className="px-4 py-2 border-r border-border/40">
              <span className="text-muted-foreground block">Closing Date</span>
              <span className="font-semibold">TBD</span>
            </div>
            <div className="px-4 py-2 border-r border-border/40">
              <span className="text-muted-foreground block">Property</span>
              <span className="font-semibold">{propertyAddress}</span>
            </div>
            <div className="px-4 py-2">
              <span className="text-muted-foreground block">Sale Price</span>
              <span className="font-semibold">{formatCurrency(mortgageData.homePrice)}</span>
            </div>
          </div>

          {/* Loan Terms */}
          <SectionHeader>Loan Terms</SectionHeader>
          <Row label="Loan Amount" value={formatCurrency(loanAmount)} />
          <Row label="Interest Rate" value={formatInterestRate(mortgageData.interestRate)} />
          <Row label="Monthly Principal & Interest" value={formatCurrency(pi)} />
          <Row
            label="Prepayment Penalty"
            value={<Badge variant="outline" className="text-xs">No</Badge>}
          />
          <Row
            label="Balloon Payment"
            value={<Badge variant="outline" className="text-xs">No</Badge>}
          />

          {/* Projected Payments */}
          <SectionHeader>Projected Payments</SectionHeader>
          <Row label="Principal & Interest" value={formatCurrency(pi)} />
          {mp.pmi > 0 && <Row label="Mortgage Insurance" value={formatCurrency(mp.pmi)} indent />}
          <Row label="Estimated Escrow" value={formatCurrency(escrow)} sub />
          <Row label={<span className="pl-4 text-xs">Property Taxes</span>} value={formatCurrency(mp.taxes)} sub />
          <Row label={<span className="pl-4 text-xs">Homeowner's Insurance</span>} value={formatCurrency(mp.insurance)} sub />
          {mp.hoa > 0 && <Row label="HOA Dues" value={formatCurrency(mp.hoa)} indent />}
          <Row label="Estimated Total Monthly Payment" value={formatCurrency(mp.total)} bold />

          {/* Costs at Closing */}
          <SectionHeader>Costs at Closing</SectionHeader>
          <Row label="Closing Costs" value={formatCurrency(grossClosingCosts)} />
          <Row label="Cash to Close" value={formatCurrency(cashToClose)} bold />

          {/* ═══════════ PAGE 2 — Loan Costs Breakdown ═══════════ */}
          <SectionHeader dark>Closing Cost Details</SectionHeader>

          <SectionHeader>A. Origination Charges</SectionHeader>
          <Row
            label={
              cc.loanOriginationWaived
                ? <span>Loan Origination Fee <Badge variant="secondary" className="ml-1 text-[10px]">Waived{cc.builderName ? ` by ${cc.builderName}` : ''}</Badge></span>
                : 'Loan Origination Fee (1%)'
            }
            value={formatCurrency(cc.loanOrigination)}
            indent
          />

          <SectionHeader>B. Services You Cannot Shop For</SectionHeader>
          <Row label="Appraisal Fee" value={formatCurrency(cc.appraisalFee)} indent />

          <SectionHeader>C. Services You Can Shop For</SectionHeader>
          <Row label="Title — Insurance & Settlement" value={formatCurrency(cc.titleServices)} indent />

          <Row label="Total Loan Costs (A + B + C)" value={formatCurrency(loanCosts)} bold />

          {/* ═══════════ PAGE 3 — Other Costs & Cash to Close ═══════════ */}
          <SectionHeader dark>Other Costs</SectionHeader>

          <SectionHeader>E. Taxes & Government Fees</SectionHeader>
          <Row label="Government Recording Charges" value={formatCurrency(cc.governmentRecordingCharges)} indent />

          <SectionHeader>F. Prepaids</SectionHeader>
          <Row label="Prepaids (taxes, insurance, interest)" value={formatCurrency(cc.prepaids)} indent />

          <SectionHeader>G. Initial Escrow Payment at Closing</SectionHeader>
          <Row label="Initial Escrow Deposit" value={formatCurrency(cc.initialEscrow)} indent />

          <SectionHeader>H. Other</SectionHeader>
          <Row label="Other Fees" value={formatCurrency(cc.other)} indent />
          {cc.fundingFee > 0 && (
            <Row label="VA/FHA Funding Fee (in closing costs)" value={formatCurrency(cc.fundingFee)} indent />
          )}

          <Row label="Total Other Costs (E + F + G + H)" value={formatCurrency(otherCosts)} bold />
          <Row label="Total Closing Costs (Loan + Other)" value={formatCurrency(grossClosingCosts)} bold />

          {/* Calculating Cash to Close */}
          <SectionHeader dark>Calculating Cash to Close</SectionHeader>
          <Row label="Total Closing Costs" value={formatCurrency(grossClosingCosts)} />
          <Row label="Down Payment / Funds from Borrower" value={formatCurrency(mortgageData.downPayment)} />
          {sellerCredit > 0 && (
            <Row label="Seller Credits" value={`(${formatCurrency(sellerCredit)})`} />
          )}
          <Row label="Estimated Cash to Close" value={formatCurrency(cashToClose)} bold />

          {/* ═══════════ PAGE 4 — Loan Calculations ═══════════ */}
          <SectionHeader dark>Loan Calculations</SectionHeader>
          <Row label="Total of Payments" value={formatCurrency(totalPayments)} />
          <Row label="Finance Charge (Total Interest + Fees)" value={formatCurrency(financeCharge)} />
          <Row label="Amount Financed" value={formatCurrency(loanAmount)} />
          <Row
            label="Annual Percentage Rate (APR)"
            value={formatInterestRate(mortgageData.interestRate)}
            sub
          />
          <div className="px-4 py-1 text-[10px] text-muted-foreground italic border-b border-border/40">
            APR shown is a simplified estimate. Actual APR may differ based on fees and compounding.
          </div>

          {/* ── footer ── */}
          <div className="px-4 py-3 text-[10px] text-muted-foreground bg-muted/30">
            This is a sample Closing Disclosure generated for illustrative purposes. It is not a
            commitment to lend and does not guarantee any terms. Consult your lender for an official
            Closing Disclosure before your scheduled closing date.
          </div>
        </div>
      )}
    </div>
  );
};

export default ClosingDisclosurePreview;
